import React from "react";
import { Panel } from "@/components/Panel";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, PieChart, Pie, Cell,
} from "recharts";
import {
  Coffee, Wifi, CreditCard, Banknote, ArrowRight, Sparkles,
  TrendingUp, TrendingDown, Target, Brain, Smartphone, Monitor, Play, Square as StopIcon, RefreshCw, Trash2,
} from "lucide-react";

// Phase 25.4 — POS Live MOBILE variant.
// Same data, same business logic, but tab-based navigation + larger touch
// targets, optimized for SMB owners checking takings on the floor from a phone.
// Tabs: TODAY → BRIEFING → HOURLY → SKUS → LIVE TAIL
// Auto-renders below md breakpoint, manually toggleable on desktop.

const PAYMENT_LABELS = {
  card_tap: "Card · Tap",
  card_chip: "Card · Chip",
  apple_pay: "Apple Pay",
  google_pay: "Google Pay",
  cash: "Cash",
};
const PAYMENT_COLORS = ["#FFB000", "#00E676", "#00BFFF", "#FF6B47", "#A78BFA"];

function fmtMoney(cents) {
  if (cents == null) return "—";
  return (cents / 100).toLocaleString("en-US", { style: "currency", currency: "USD" });
}
function fmtTime(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

const TABS = [
  { key: "today", label: "TODAY", icon: Coffee },
  { key: "ai", label: "AI BRIEF", icon: Brain },
  { key: "hourly", label: "HOURLY", icon: TrendingUp },
  { key: "skus", label: "TOP SKUS", icon: Target },
  { key: "tail", label: "LIVE TAIL", icon: Wifi },
];

export default function POSLiveMobile({
  conn, today, hourly, recent, playbook, loading,
  hasAnyData, streaming, sourceLabel, provider,
  refreshingAI, onRefresh, onRefreshAI, onStartDemo, onStopDemo, onClearDemo,
  onViewSwitch, busy,
}) {
  const [tab, setTab] = React.useState("today");
  const revenue = today?.total_revenue_cents ?? today?.revenue_cents ?? 0;
  const txnCount = today?.txn_count ?? 0;
  const avgTicket = today?.avg_ticket_cents ?? 0;
  const tips = today?.tip_cents ?? today?.total_tip_cents ?? 0;

  // Payment-mix donut data
  const paymentMix = today?.by_payment_type
    ? Object.entries(today.by_payment_type).map(([k, v]) => ({
        name: PAYMENT_LABELS[k] || k,
        value: v?.revenue_cents ?? v?.count ?? v ?? 0,
        rawKey: k,
      })).filter(d => d.value > 0)
    : [];

  return (
    <div className="min-h-screen bg-black text-white" data-testid="pos-live-mobile">
      {/* Sticky compact header */}
      <header className="sticky top-0 z-10 bg-black/95 backdrop-blur border-b border-zinc-900">
        <div className="px-4 py-3 flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">// POS LIVE</span>
              {streaming && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-[#00E676]/40 bg-[#00E676]/10 text-[#00E676] font-mono text-[9px] uppercase tracking-widest">
                  <span className="w-1 h-1 rounded-full bg-[#00E676] animate-pulse" /> LIVE
                </span>
              )}
              {sourceLabel && (
                <span className="px-1.5 py-0.5 border border-zinc-800 bg-zinc-950 text-zinc-400 font-mono text-[9px] uppercase tracking-widest">
                  {sourceLabel}
                </span>
              )}
            </div>
            <div className="font-heading font-black text-2xl tracking-tighter text-[#FFB000] mt-0.5 truncate" data-testid="mobile-revenue-headline">
              {fmtMoney(revenue)}
            </div>
            <div className="font-mono text-[9px] text-zinc-600">
              {txnCount} txns · avg {fmtMoney(avgTicket)}
            </div>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <button
              onClick={onRefresh}
              disabled={loading}
              data-testid="mobile-pos-refresh"
              className="p-2.5 border border-zinc-800 hover:border-zinc-700"
              aria-label="Refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
            {onViewSwitch && (
              <button
                onClick={onViewSwitch}
                data-testid="mobile-view-switch"
                className="p-2.5 border border-zinc-800 hover:border-zinc-700"
                aria-label="Desktop view"
                title="Switch to desktop view"
              >
                <Monitor className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Tab bar (horizontal scroll if narrow) */}
        <div className="flex border-t border-zinc-900 overflow-x-auto no-scrollbar">
          {TABS.map(t => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`mobile-tab-${t.key}`}
                className={`flex-1 min-w-[80px] py-3 flex flex-col items-center justify-center gap-1 border-b-2 transition-colors ${
                  active
                    ? "border-[#FFB000] text-[#FFB000] bg-[#FFB000]/5"
                    : "border-transparent text-zinc-500 hover:text-white"
                }`}
              >
                <Icon className="w-4 h-4" />
                <span className="font-mono text-[9px] uppercase tracking-widest">{t.label}</span>
              </button>
            );
          })}
        </div>
      </header>

      <div className="p-4 pb-24" data-testid={`mobile-tab-content-${tab}`}>
        {/* Empty state — no demo started yet */}
        {!loading && !hasAnyData && (
          <div className="border border-zinc-900 bg-zinc-950/40 p-6 text-center">
            <Coffee className="w-10 h-10 text-[#FFB000] mx-auto mb-3" />
            <h3 className="font-heading font-black text-xl tracking-tight">Try DARA POS Live</h3>
            <p className="text-xs text-zinc-400 mt-2 leading-relaxed">
              Sample data: a busy coffee shop. Streams ~1 new txn every 10-15s.
            </p>
            <button
              onClick={onStartDemo}
              disabled={busy}
              data-testid="mobile-pos-demo-start"
              className="mt-4 inline-flex items-center justify-center gap-2 bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-5 py-3 hover:opacity-90 disabled:opacity-50 w-full"
            >
              <Play className="w-4 h-4" />
              START DEMO
            </button>
          </div>
        )}

        {hasAnyData && (
          <>
            {tab === "today" && (
              <div className="space-y-3" data-testid="mobile-today">
                <KPICard label="Revenue" value={fmtMoney(revenue)} accent="#FFB000" big />
                <div className="grid grid-cols-3 gap-2">
                  <KPICard label="Txns" value={txnCount.toLocaleString()} />
                  <KPICard label="Avg Ticket" value={fmtMoney(avgTicket)} />
                  <KPICard label="Tips" value={fmtMoney(tips)} accent="#00E676" />
                </div>

                {paymentMix.length > 0 && (
                  <div className="border border-zinc-900 bg-zinc-950/40 p-4">
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3">
                      // PAYMENT MIX
                    </div>
                    <div className="h-44">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie data={paymentMix} cx="50%" cy="50%" innerRadius={36} outerRadius={70} paddingAngle={2} dataKey="value">
                            {paymentMix.map((_, i) => (<Cell key={i} fill={PAYMENT_COLORS[i % PAYMENT_COLORS.length]} />))}
                          </Pie>
                          <Tooltip contentStyle={{ backgroundColor: "#000", border: "1px solid #27272a", fontSize: 11 }} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="mt-2 space-y-1.5">
                      {paymentMix.map((p, i) => (
                        <div key={p.rawKey} className="flex items-center justify-between font-mono text-[11px]">
                          <span className="flex items-center gap-2">
                            <span className="w-2 h-2" style={{ background: PAYMENT_COLORS[i % PAYMENT_COLORS.length] }} />
                            <span className="text-zinc-300">{p.name}</span>
                          </span>
                          <span className="text-zinc-400">{fmtMoney(p.value)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Quick demo controls — only if demo mode */}
                {provider?.is_demo && (
                  <div className="flex items-center gap-2 mt-2">
                    {streaming ? (
                      <button onClick={onStopDemo} disabled={busy} data-testid="mobile-pos-stop"
                        className="flex-1 inline-flex items-center justify-center gap-2 border border-[#FF6B47]/40 text-[#FF6B47] font-mono text-[11px] uppercase tracking-widest px-3 py-2.5">
                        <StopIcon className="w-4 h-4" /> PAUSE DEMO
                      </button>
                    ) : (
                      <button onClick={onStartDemo} disabled={busy} data-testid="mobile-pos-resume"
                        className="flex-1 inline-flex items-center justify-center gap-2 bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-3 py-2.5">
                        <Play className="w-4 h-4" /> RESUME
                      </button>
                    )}
                    <button onClick={onClearDemo} disabled={busy}
                      className="px-3 py-2.5 border border-zinc-800 text-zinc-500"
                      aria-label="Clear demo">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            )}

            {tab === "ai" && (
              <div className="space-y-3" data-testid="mobile-ai-briefing">
                {!playbook && (
                  <div className="border border-zinc-900 bg-zinc-950/40 p-6 text-center">
                    <Sparkles className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
                    <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-600">No briefing yet</div>
                    <button onClick={onRefreshAI} disabled={refreshingAI} data-testid="mobile-ai-refresh"
                      className="mt-4 inline-flex items-center gap-2 bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-4 py-2.5">
                      <Brain className="w-4 h-4" />
                      {refreshingAI ? "GENERATING…" : "GENERATE DARA BRIEFING"}
                    </button>
                  </div>
                )}
                {playbook && (
                  <>
                    <div className="border border-[#FFB000]/30 bg-[#FFB000]/[0.04] p-4">
                      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#FFB000] mb-2 flex items-center gap-2">
                        <Brain className="w-3 h-3" /> // DARA BRIEFING · DARA ENGINE
                      </div>
                      <p className="text-sm text-zinc-200 leading-relaxed">{playbook.summary || playbook.narrative || "—"}</p>
                    </div>

                    {(playbook.cod || playbook.close_of_day) && (
                      <CloseOfDayCard cod={playbook.cod || playbook.close_of_day} />
                    )}

                    {(playbook.drivers || []).length > 0 && (
                      <div className="border border-zinc-900 bg-zinc-950/40 p-4">
                        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">// DRIVERS</div>
                        <ul className="space-y-2">
                          {(playbook.drivers || []).map((d, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-zinc-300 leading-relaxed">
                              <span className="text-[#FFB000] flex-shrink-0 mt-0.5">•</span>
                              <span>{typeof d === "string" ? d : d.text || d.title || JSON.stringify(d)}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {(playbook.actions || []).length > 0 && (
                      <div className="border border-[#00E676]/30 bg-[#00E676]/[0.04] p-4">
                        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#00E676] mb-2">// RECOMMENDED ACTIONS</div>
                        <ul className="space-y-2">
                          {(playbook.actions || []).map((a, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-zinc-200 leading-relaxed">
                              <ArrowRight className="w-3.5 h-3.5 text-[#00E676] flex-shrink-0 mt-0.5" />
                              <span>{typeof a === "string" ? a : a.text || a.title || JSON.stringify(a)}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <button onClick={onRefreshAI} disabled={refreshingAI}
                      className="w-full inline-flex items-center justify-center gap-2 border border-zinc-800 hover:border-zinc-700 px-3 py-2.5 font-mono text-[11px] uppercase tracking-widest text-zinc-300">
                      <RefreshCw className={`w-3.5 h-3.5 ${refreshingAI ? "animate-spin" : ""}`} />
                      {refreshingAI ? "REGENERATING…" : "REGENERATE BRIEFING"}
                    </button>
                  </>
                )}
              </div>
            )}

            {tab === "hourly" && (
              <div className="space-y-3" data-testid="mobile-hourly">
                <div className="border border-zinc-900 bg-zinc-950/40 p-4">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3">
                    // HOURLY · TODAY
                  </div>
                  <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={hourly} margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
                        <XAxis dataKey="hour" stroke="#52525b" fontSize={9} tickFormatter={(v) => {
                          const d = typeof v === "string" ? new Date(v) : v;
                          return d?.toLocaleTimeString?.("en-US", { hour: "numeric" }) || v;
                        }} />
                        <YAxis stroke="#52525b" fontSize={10} />
                        <Tooltip contentStyle={{ backgroundColor: "#000", border: "1px solid #27272a", fontSize: 11 }}
                          formatter={(v, n) => n === "revenue_cents" ? fmtMoney(v) : v} />
                        <Bar dataKey="txn_count" fill="#FFB000" radius={[2, 2, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            )}

            {tab === "skus" && (
              <div className="space-y-2" data-testid="mobile-top-skus">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">// TOP SKUS · TODAY</div>
                {(today?.top_skus || []).length === 0 && (
                  <div className="text-center py-8 font-mono text-[10px] text-zinc-700">No SKUs yet today.</div>
                )}
                {(today?.top_skus || []).map((s, i) => (
                  <div key={i} className="border border-zinc-900 bg-zinc-950/40 p-3 flex items-center justify-between gap-3" data-testid={`mobile-sku-${i}`}>
                    <div className="min-w-0 flex-1">
                      <div className="font-heading font-bold text-sm text-white truncate">{s.sku || s.name || "—"}</div>
                      <div className="font-mono text-[10px] text-zinc-600 mt-0.5">{s.qty || s.count || 0} sold</div>
                    </div>
                    <div className="font-heading font-black text-base text-[#FFB000] tracking-tighter flex-shrink-0">
                      {fmtMoney(s.revenue_cents)}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {tab === "tail" && (
              <div className="space-y-2" data-testid="mobile-tail">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">// LIVE TRANSACTIONS</div>
                  <button onClick={onRefresh} className="text-zinc-500 hover:text-white p-1" data-testid="mobile-tail-refresh">
                    <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
                  </button>
                </div>
                {recent.length === 0 && (
                  <div className="text-center py-8 font-mono text-[10px] text-zinc-700">No transactions yet.</div>
                )}
                {recent.map((t) => {
                  const Icon = t.payment_type === "cash" ? Banknote : CreditCard;
                  return (
                    <div key={t.id || t.transaction_id || t.external_id} className="border border-zinc-900 bg-zinc-950/40 p-3 flex items-center justify-between gap-3" data-testid={`mobile-txn-${t.id || t.transaction_id || t.external_id}`}>
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <Icon className="w-4 h-4 text-zinc-500 flex-shrink-0" />
                        <div className="min-w-0">
                          <div className="font-mono text-xs text-white truncate">
                            {(t.items || []).map(i => i.sku || i.name).slice(0, 2).join(", ") || PAYMENT_LABELS[t.payment_type] || t.payment_type || "Transaction"}
                          </div>
                          <div className="font-mono text-[9px] text-zinc-600">{fmtTime(t.occurred_at)}</div>
                        </div>
                      </div>
                      <div className="font-heading font-black text-base text-[#FFB000] tracking-tighter flex-shrink-0">
                        {fmtMoney(t.total_cents)}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function KPICard({ label, value, accent = "#FFFFFF", big = false }) {
  return (
    <div className={`border border-zinc-900 bg-zinc-950/40 ${big ? "p-5" : "p-3"}`}>
      <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500 mb-1">{label}</div>
      <div className={`font-heading font-black tracking-tighter ${big ? "text-4xl" : "text-xl"}`} style={{ color: accent }}>
        {value}
      </div>
    </div>
  );
}

function CloseOfDayCard({ cod }) {
  const proj = cod.projection_cents || 0;
  const delta = cod.delta_vs_baseline_pct;
  const up = (delta || 0) >= 0;
  return (
    <div className="border border-[#FFB000]/30 bg-[#FFB000]/[0.04] p-4" data-testid="mobile-cod-card">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">// CLOSE-OF-DAY PROJECTION</div>
      <div className="flex items-baseline gap-3">
        <div className="font-heading font-black text-3xl tracking-tighter text-[#FFB000]">{fmtMoney(proj)}</div>
        {delta != null && (
          <div className={`font-mono text-xs inline-flex items-center gap-1 ${up ? "text-[#00E676]" : "text-[#FF6B47]"}`}>
            {up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            {delta > 0 ? "+" : ""}{delta}%
          </div>
        )}
      </div>
      {cod.baseline_avg_revenue_cents && (
        <div className="font-mono text-[10px] text-zinc-600 mt-2">
          vs same-DOW avg {fmtMoney(cod.baseline_avg_revenue_cents)} · method {(cod.method || "").replace(/_/g, " ")}
        </div>
      )}
    </div>
  );
}
